import {
	SFT_ThinkTech_AI_Automation_01,
	SFT_ThinkTech_AI_Automation_02,
	SFT_ThinkTech_AI_Automation_03,
	SFT_ThinkTech_AI_Automation_04,
	SFT_ThinkTech_AI_Automation_05,
	SFT_ThinkTech_AI_Automation_06,
	SFT_ThinkTech_AI_Automation_07,
	SFT_ThinkTech_Product_Strategy_01,
	SFT_ThinkTech_Product_Strategy_02,
	SFT_ThinkTech_Product_Strategy_03,
	SFT_ThinkTech_Product_Strategy_04,
	FIEM_MERN_Cohort_01,
	FIEM_MERN_Cohort_02,
	FIEM_MERN_Handover_01,
	FIEM_MERN_Handover_02,
	FIEM_MERN_Handover_03,
	Aeonix_Certificate_Of_Achievement_Redacted
} from "../assets";
import { workshops } from "../content";
import type { Workshop, WorkshopCertificate, WorkshopImage } from "../types/content";

/**
 * ============================================================================
 * Workshops — the certificate image slot. POPULATED as of D-079, with a
 * redacted derivative. The scan it was derived from is still on disk and must
 * never be exported from here.
 * ============================================================================
 *
 * Same mechanism as `src/utils/achievementImages.ts`: `src/content/data/*.json`
 * is inert data with no module graph, so a path written there never reaches
 * Vite's asset pipeline — it would ship unhashed, outside the build's own
 * accounting, or simply 404. An imported binding is content-hashed into
 * `build/assets/` and the leak sweep walks it.
 *
 * ----------------------------------------------------------------------------
 * TWO FILES, AND ONLY ONE OF THEM MAY BE NAMED HERE
 * ----------------------------------------------------------------------------
 * `src/assets/workshop/FIEM-MERN/` holds the ORIGINAL scan, committed since
 * D-076. It was opened and read on 2026-08-23, on 2026-08-24, and again before
 * this line was written; it carries third-party business and personal details,
 * in legible type, that are not the site owner's to publish. **It is imported
 * by nothing, and an `export` line naming it is the entire distance between
 * "on disk" and "on the internet"** — that is what D-018/D-020 established
 * about barrel re-exports, and a folder is not a publish boundary.
 *
 * What ships instead is a derivative built on 2026-08-24: the same page at the
 * same native size with four regions replaced in the raster, re-encoded through
 * a canvas, which is also what drops the source file's metadata. **Which
 * regions, and the evidence that each one is actually gone rather than covered,
 * is in D-079** — see the next paragraph for why it is there and not here.
 *
 * The five photographs beside the original are barred for a stronger reason and
 * are not merely awaiting a size: they carry other people's names, credential
 * identifiers and faces. Owner ruling 2026-08-24: **withhold all five.**
 *
 * **WHY THE FIELD LIST IS IN DECISIONS AND NOT IN THIS FILE — on purpose.**
 * `docs/` is not bundled; this comment is. Vite writes `sourcesContent` into
 * the deployed sourcemap, so every word here is fetchable from the live site —
 * D-063 found two people's real profiles readable that way while rendering
 * nowhere, and D-076 found the WORD "LLPIN" in the deployed map for the same
 * reason. An itemised list of the sensitive fields in a named file in a public
 * repository is a map, and it does not need to ship to do its job here.
 *
 * ----------------------------------------------------------------------------
 * THE GATE IS TWO KEYS AND BOTH ARE REQUIRED
 * ----------------------------------------------------------------------------
 * `certificate.status` in `workshops.json` says whether an image MAY be shown;
 * `WORKSHOP_IMAGES` below says whether one EXISTS. D-065 is the record of what
 * happens when those two questions are answered in different files and only one
 * of them is checked — a section rendered 9 of 21 credentials for months with
 * every individual control working correctly.
 *
 * So they are checked against each other, here, at module load, which fails
 * `npm run build` because the pre-render imports this module (the D-071
 * mechanism). A `published` status with no binary throws; a binary with a
 * non-published status throws. Neither can ship.
 *
 * ----------------------------------------------------------------------------
 * TO PUBLISH A FURTHER CERTIFICATE
 * ----------------------------------------------------------------------------
 *   1. Redact it, and **verify the redaction out of the raster** rather than
 *      trusting that the boxes were drawn: interiors read back after
 *      re-encoding, an edge band outside every box measured for surviving ink,
 *      and anything that reports ink magnified and looked at. D-079 records the
 *      protocol and the instrument, including the planted known-bad that proves
 *      the edge check can report presence.
 *   2. Keep it web-sized and put it in `src/assets/workshop/`.
 *   3. Export it from `src/assets/index.ts`.
 *   4. Add it to `WORKSHOP_IMAGES` below, keyed by `certificate.id`.
 *   5. Flip `status` to `"published"` in `workshops.json`.
 *
 * Steps 4 and 5 must happen together; the assertion below is what enforces it.
 */

/**
 * Certificate id -> imported binary.
 *
 * DO NOT READ THE COMMENT ABOVE AS A DECLARATION. D-074 counted one entry in
 * the equivalent martial-arts map when it held zero, because a naive scan of
 * the raw source matched the module's JSDoc example — which used a real id and
 * therefore looked exactly like a real entry. Any gate that counts these must
 * strip comments first and match the declaration body.
 */
export const WORKSHOP_IMAGES: Record<string, string> = {
	"aeonix-mern-certificate-of-achievement": Aeonix_Certificate_Of_Achievement_Redacted
};

/** The binary for a certificate id, or undefined if none is exported. */
export const workshopCertificateImage = (id: string): string | undefined => WORKSHOP_IMAGES[id];

/* -------------------------------------------------------------------------- */
/* The two-key invariant, enforced at module load so it fails the build.       */
/* -------------------------------------------------------------------------- */
const declared: WorkshopCertificate[] = workshops
	.map((w: Workshop) => w.certificate)
	.filter((c: WorkshopCertificate | null): c is WorkshopCertificate => c !== null);

for (const certificate of declared) {
	const binary = WORKSHOP_IMAGES[certificate.id];
	if (certificate.status === "published" && binary === undefined) {
		throw new Error(
			`workshopImages: certificate "${certificate.id}" is marked published in workshops.json but is exported from no module. ` +
				"Add it to WORKSHOP_IMAGES, or set status back to pending-redaction."
		);
	}
	if (certificate.status !== "published" && binary !== undefined) {
		throw new Error(
			`workshopImages: certificate "${certificate.id}" has a binary in WORKSHOP_IMAGES but status "${certificate.status}". ` +
				"An unredacted scan must not be reachable — remove the export, or redact the file and publish it deliberately."
		);
	}
}

/* And the other direction: a binary keyed to nothing is a file shipping in the
   bundle that no page can reach, which is the orphan half of D-065's join. */
const declaredIds = new Set(declared.map((c: WorkshopCertificate) => c.id));
for (const key of Object.keys(WORKSHOP_IMAGES)) {
	if (!declaredIds.has(key)) {
		throw new Error(
			`workshopImages: "${key}" is exported here but names no certificate in workshops.json. ` +
				"An orphaned binary ships in the bundle and renders nowhere."
		);
	}
}

/* ========================================================================== */
/* THE GALLERY — SAME GATE, DIFFERENT PICTURES (D-081)                        */
/* ========================================================================== */

/**
 * Workshop photograph id -> imported binary.
 *
 * **ELEVEN ENTRIES AS OF D-084, AND NINETEEN FILES WERE SUPPLIED.** The eight
 * that are absent are absent for a reason no resize addresses: they show a
 * lecture theatre of identifiable attendees, and consent for a room is not the
 * owner's to give. The four FIEM originals are absent for a stronger reason
 * still — a registration identifier and a director's signature. Absence here
 * IS the control: an `export` line is the whole distance between a file being
 * on disk and being on the internet (D-018, D-079), and the invariants below
 * make the absence loud rather than quiet in the other direction too.
 *
 * The four Product Strategy frames were held until D-084 on an identity
 * question — D-082 could not tell whether the speaker in the white cap was the
 * owner or a colleague, and publishing a colleague as the owner is a worse
 * error than publishing nothing. The owner confirmed it. **That is why they
 * are here and it is not visible in this file**, which is what DECISIONS is
 * for.
 *
 * The same JSDoc warning as above applies — D-074 counted one entry in a map
 * that held zero, because a gate matched the example in a comment. Any gate
 * counting these must strip comments and read the declaration body.
 */
export const WORKSHOP_GALLERY_IMAGES: Record<string, string> = {
	"sft-thinktech-ai-automation-01": SFT_ThinkTech_AI_Automation_01,
	"sft-thinktech-ai-automation-02": SFT_ThinkTech_AI_Automation_02,
	"sft-thinktech-ai-automation-03": SFT_ThinkTech_AI_Automation_03,
	"sft-thinktech-ai-automation-04": SFT_ThinkTech_AI_Automation_04,
	"sft-thinktech-ai-automation-05": SFT_ThinkTech_AI_Automation_05,
	"sft-thinktech-ai-automation-06": SFT_ThinkTech_AI_Automation_06,
	"sft-thinktech-ai-automation-07": SFT_ThinkTech_AI_Automation_07,
	"sft-thinktech-product-strategy-01": SFT_ThinkTech_Product_Strategy_01,
	"sft-thinktech-product-strategy-02": SFT_ThinkTech_Product_Strategy_02,
	"sft-thinktech-product-strategy-03": SFT_ThinkTech_Product_Strategy_03,
	"sft-thinktech-product-strategy-04": SFT_ThinkTech_Product_Strategy_04,
	"fiem-mern-cohort-01": FIEM_MERN_Cohort_01,
	"fiem-mern-cohort-02": FIEM_MERN_Cohort_02,
	"fiem-mern-handover-01": FIEM_MERN_Handover_01,
	"fiem-mern-handover-02": FIEM_MERN_Handover_02,
	"fiem-mern-handover-03": FIEM_MERN_Handover_03
};

/** The binary for a photograph id, or undefined if none is exported. */
export const workshopGalleryImage = (id: string): string | undefined => WORKSHOP_GALLERY_IMAGES[id];

/* The two-key invariant again, over photographs. Deliberately a second loop
   rather than a generalisation of the first: the two maps hold different
   things, fail for different reasons, and the error a future reader gets
   should name the one they actually broke. */
const declaredImages: WorkshopImage[] = workshops.flatMap((w: Workshop) => w.images);

const seenImageIds = new Set<string>();
for (const image of declaredImages) {
	if (seenImageIds.has(image.id)) {
		throw new Error(
			`workshopImages: two workshop photographs share the id "${image.id}". ` +
				"Ids key into WORKSHOP_GALLERY_IMAGES, so one would render the other's picture."
		);
	}
	seenImageIds.add(image.id);

	const binary = WORKSHOP_GALLERY_IMAGES[image.id];
	if (image.status === "published" && binary === undefined) {
		throw new Error(
			`workshopImages: photograph "${image.id}" is marked published in workshops.json but is ` +
				"exported from no module. Add it to WORKSHOP_GALLERY_IMAGES, or set status back to withheld."
		);
	}
	if (image.status !== "published" && binary !== undefined) {
		throw new Error(
			`workshopImages: photograph "${image.id}" has a binary in WORKSHOP_GALLERY_IMAGES but status ` +
				`"${image.status}". A withheld photograph must not be reachable — remove the export, or ` +
				"clear it for publication deliberately."
		);
	}
}

for (const key of Object.keys(WORKSHOP_GALLERY_IMAGES)) {
	if (!seenImageIds.has(key)) {
		throw new Error(
			`workshopImages: "${key}" is exported here but names no photograph in workshops.json. ` +
				"An orphaned binary ships in the bundle and renders nowhere."
		);
	}
}

/** A photograph that is cleared to publish AND has a binary behind it. */
export interface ResolvedWorkshopImage {
	image: WorkshopImage;
	binary: string;
}

/**
 * The published, exported subset of a workshop's photographs — the ONE place
 * that rule is written (D-095).
 *
 * BOTH KEYS, PER IMAGE, and neither is optional. `status` in `workshops.json`
 * says whether a photograph MAY be shown; presence in `WORKSHOP_GALLERY_IMAGES`
 * says whether one EXISTS. A withheld photograph and a published one with no
 * binary both drop out here, so no caller can render a broken thumbnail or a
 * picture nobody cleared.
 *
 * It was `WorkshopGallery`'s private filter until the /workshops index gained
 * an image glimpse and became the second caller. Two copies of a publish rule
 * is the shape this repository keeps paying for — D-089 is five credentials
 * that sat at `"category": "ai"` while the renderer read a different field —
 * and the cost here would be a face on a card that an owner ruling withheld.
 * The module-load assertions above still fail the build if the two keys ever
 * disagree; this function is what every renderer reads instead of re-deriving.
 */
export const resolvePublishedImages = (images: WorkshopImage[]): ResolvedWorkshopImage[] =>
	images
		.map((image: WorkshopImage) => ({ image, binary: workshopGalleryImage(image.id) }))
		.filter(
			(entry): entry is ResolvedWorkshopImage => entry.image.status === "published" && entry.binary !== undefined
		);
